// webserver/src/components/Settings/Settings.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import './Settings.css';
import LgDeviceAppEditor from './LgDeviceAppEditor';
import PlayerOnlySettings from '../Controls/PlayerOnlySettings';
import { isLgPlayer, visiblePhysicalDevices } from '../../utils/lgPlayer';
import ConfirmSite from '../ConfirmSite';
import GoogleHomeDevices from '../GoogleHome/GoogleHomeDevices';
import WatermarkSettings from './WatermarkSettings';
import ShortcutsSettings from './ShortcutsSettings';
import MySubscriptionSettings from './MySubscriptionSettings';
import UsersSettings from './UsersSettings';
import DestinationSetup from './DestinationSetup';
import SocialIntegrations from './SocialIntegrations';
import WifiSettings from '../WifiSettings/WifiSettings';
import WifiTab from '../Dashboard/WifiTab';
import Plugins from '../Events';
import useCanvaConnection from '../../hooks/useCanvaConnection';
import useGoogleDriveConnection from '../../hooks/useGoogleDriveConnection';
import useYouTubeConnection from '../../hooks/useYouTubeConnection';
import { useEntitlements } from '../../context/EntitlementsContext';
import { useNudge } from '../../context/NudgeContext';
import { useSocket } from '../../context/SocketContext';
import { getIsAccountManagerFromToken, getUserRoleFromToken } from '../../utils/jwt';
import {
  getEnabledPluginKeys,
  loadEnabledPluginKeys,
  persistPluginEnabled,
  subscribeToPluginPreferences,
} from '../../utils/pluginPreferences';

function Settings({
  site,
  sites = [],
  sharedDestinations = [],
  area,
  entityMode = 'venue',
  refreshSitesAreasDevices,
  onUpdateSiteFields = null,
  currentDevice,
  assistantLoginTrigger = 0,
  requestedSubTab = null,
  requestedDeviceId = null,
  requestedDeviceRequestKey = 0,
  openEventInstanceId = null,
  onConsumeOpenEventRequest = null,
  siteDetailsPlaceId = null,
  onConsumeSiteDetailsPlaceId = null,
}) {
  const isAdmin = getUserRoleFromToken(localStorage.getItem('token')) === 'admin';
  const isDestinationMode = entityMode === 'destination';
  const selectedSiteIsDestination =
    String(site?.site_type || '').trim().toLowerCase() === 'destination';
  const firstDestinationSite = useMemo(
    () => (Array.isArray(sites)
      ? sites.find((entry) => String(entry?.site_type || '').trim().toLowerCase() === 'destination') || null
      : null),
    [sites]
  );
  const hasConfiguredDestination = Boolean(firstDestinationSite);
  const aiAgentsSite = selectedSiteIsDestination ? site : firstDestinationSite;
  const siteLabel = isDestinationMode ? 'Destination' : 'Site';
  const detailsTabLabel = isDestinationMode ? 'Destination Details' : 'Site Details';
  const mapsTabLabel = isDestinationMode ? 'Destination Maps' : 'Site Maps';
  const showDestinationSetup = isAdmin
    && (isDestinationMode || selectedSiteIsDestination || !hasConfiguredDestination);
  const showAiAgentsTab = isAdmin;
  const socket = useSocket();
  const [selectedSubTab, setSelectedSubTab] = useState('Shortcuts');
  const handledDeviceRequestKeyRef = useRef(0);
  const [newDestinationName, setNewDestinationName] = useState('');
  const [newDestinationCountryCode, setNewDestinationCountryCode] = useState('');
  const [creatingDestination, setCreatingDestination] = useState(false);
  const [oneTimeSiteDetailsPlaceId, setOneTimeSiteDetailsPlaceId] = useState(siteDetailsPlaceId);
  const [message, setMessage] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifiedCode, setVerifiedCode] = useState('');
  const [deviceStatusMsg, setDeviceStatusMsg] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceIP, setDeviceIP] = useState('');
  const [deviceType, setDeviceType] = useState('Music Player');
  // App-only startup settings (only shown + persisted when the connecting device is the Android TV app).
  const [verifiedClientIsApp, setVerifiedClientIsApp] = useState(false);
  const [startupMode, setStartupMode] = useState('AUTO_START');
  const [allowLocalMenu, setAllowLocalMenu] = useState(true);
  const [menuPin, setMenuPin] = useState('');
  const [devices, setDevices] = useState([]);
  const [irBridges, setIrBridges] = useState([]);
  const [showDeviceDetailsForm, setShowDeviceDetailsForm] = useState(false);
  const [addTVMode, setAddTVMode] = useState(false);
  const [addSourceMode, setAddSourceMode] = useState(false);
  const [isIRBridge, setIsIRBridge] = useState(false);
  const [activeAddPanel, setActiveAddPanel] = useState(null);
  const [tvControlDeviceType, setTvControlDeviceType] = useState('');
  const [kodiHosts, setKodiHosts] = useState([]);
  const [kodiHostDeviceId, setKodiHostDeviceId] = useState('');
  const [kodiHost, setKodiHost] = useState('127.0.0.1');
  const [kodiPort, setKodiPort] = useState('8080');
  const [kodiUsername, setKodiUsername] = useState('kodi');
  const [kodiPassword, setKodiPassword] = useState('9999');
  const [kodiPasswordConfigured, setKodiPasswordConfigured] = useState(false);
  const [kodiAuthenticationEnabled, setKodiAuthenticationEnabled] = useState(false);
  const [kodiStatus, setKodiStatus] = useState('');
  const [kodiBusy, setKodiBusy] = useState(false);
  // Onboarding wizard state
  const [onboardingStep, setOnboardingStep] = useState(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardingLogs, setOnboardingLogs] = useState([]);
  const [newDeviceId, setNewDeviceId] = useState(null);
  const [onboardingIP, setOnboardingIP] = useState('');
  const commandIdRef = React.useRef(1);
  const { connected: canvaConnected, connect: connectCanva, disconnect: disconnectCanva } = useCanvaConnection();
  const { status: googleDriveStatus, connect: connectGoogleDrive, disconnect: disconnectGoogleDrive } = useGoogleDriveConnection(site?.id);
  const { status: youtubeStatus, connect: connectYouTube, disconnect: disconnectYouTube } = useYouTubeConnection(site?.id);
  const connectUrl = typeof window !== 'undefined' ? `${window.location.origin}/connect` : '/connect';
  const [connectQrDataUrl, setConnectQrDataUrl] = useState('');
  const { entitlements, limits, refresh: refreshEntitlements, subscriptionType } = useEntitlements();
  const canManageAccountUsers =
    (getIsAccountManagerFromToken(localStorage.getItem('token')) || isAdmin) &&
    String(subscriptionType || 'free').toLowerCase() !== 'free';
  const { showNudge } = useNudge();
  const [gaMeasurementId, setGaMeasurementId] = useState('');
  const [fbPixelId, setFbPixelId] = useState('');
  const [clarityProjectId, setClarityProjectId] = useState('');
  const [tiktokPixelId, setTiktokPixelId] = useState('');
  const [gaSavedId, setGaSavedId] = useState('');
  const [fbSavedId, setFbSavedId] = useState('');
  const [claritySavedId, setClaritySavedId] = useState('');
  const [tiktokSavedId, setTiktokSavedId] = useState('');
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaSaving, setGaSaving] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbSaving, setFbSaving] = useState(false);
  const [clarityLoading, setClarityLoading] = useState(false);
  const [claritySaving, setClaritySaving] = useState(false);
  const [tiktokLoading, setTiktokLoading] = useState(false);
  const [tiktokSaving, setTiktokSaving] = useState(false);
  const [analyticsLoadError, setAnalyticsLoadError] = useState('');
  const [analyticsSaveError, setAnalyticsSaveError] = useState('');
  const [enabledPluginKeys, setEnabledPluginKeys] = useState(() =>
    getEnabledPluginKeys(site?.id || null)
  );
  const platformGa4Id = process.env.REACT_APP_PLATFORM_GA4_ID || '';
  const platformFbPixelId = process.env.REACT_APP_PLATFORM_FB_PIXEL_ID || '';
  const platformClarityId = process.env.REACT_APP_PLATFORM_CLARITY_ID || '';
  const platformTiktokPixelId = process.env.REACT_APP_PLATFORM_TIKTOK_PIXEL_ID || '';

  useEffect(() => {
    let cancelled = false;
    const refreshEnabledPluginKeys = () => {
      if (cancelled) return;
      setEnabledPluginKeys(getEnabledPluginKeys(site?.id || null));
    };
    refreshEnabledPluginKeys();
    if (site?.id) {
      loadEnabledPluginKeys(site.id).then(refreshEnabledPluginKeys);
    }
    const unsubscribe = subscribeToPluginPreferences(refreshEnabledPluginKeys);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [site?.id]);

  const togglePluginEnabled = async (pluginKey, enabled) => {
    try {
      setEnabledPluginKeys(await persistPluginEnabled(site?.id || null, pluginKey, enabled));
      setMessage(enabled ? 'Plugin enabled.' : 'Plugin disabled.');
    } catch (error) {
      setMessage(error.message || 'Failed to save plugin setting.');
    }
  };

  // App selection (Android TV app clients)
  const [showAppSelectionModal, setShowAppSelectionModal] = useState(false);
  const [appSelectionDeviceId, setAppSelectionDeviceId] = useState(null);
  const [appSelectionApps, setAppSelectionApps] = useState([]);
  const [appSelectionEnabled, setAppSelectionEnabled] = useState(new Set());
  const [appSelectionStatus, setAppSelectionStatus] = useState('idle'); // idle | loading | ready | error | saving
  const [appSelectionError, setAppSelectionError] = useState('');

  // Device edit modal (startup + apps)
  const [showDeviceEditModal, setShowDeviceEditModal] = useState(false);
  const [deviceEditDevice, setDeviceEditDevice] = useState(null);
  const [lgEditDevice, setLgEditDevice] = useState(null);
  const [playerEditDevice, setPlayerEditDevice] = useState(null);
  useEffect(() => { setLgEditDevice(null); }, [site?.id, area?.id]);
  const [deviceEditStartupMode, setDeviceEditStartupMode] = useState('AUTO_START');
  const [deviceEditAllowLocalMenu, setDeviceEditAllowLocalMenu] = useState(true);
  const [deviceEditEnsureBackgroundService, setDeviceEditEnsureBackgroundService] = useState(false);
  const [deviceEditMenuPin, setDeviceEditMenuPin] = useState('');
  const [deviceEditApps, setDeviceEditApps] = useState([]);
  const [deviceEditAppsLoaded, setDeviceEditAppsLoaded] = useState(false);
  const [deviceEditEnabled, setDeviceEditEnabled] = useState(new Set());
  const [deviceEditStatus, setDeviceEditStatus] = useState('idle'); // idle | loading | ready | saving | error
  const [deviceEditError, setDeviceEditError] = useState('');
  const [reconnectDevice, setReconnectDevice] = useState(null);
  const [reconnectCode, setReconnectCode] = useState('');
  const [reconnectStatus, setReconnectStatus] = useState('idle');
  const [reconnectMessage, setReconnectMessage] = useState('');

  const isSuitestreamApp = (app) => {
    const pkg = String(app?.package_name || '').toLowerCase();
    const name = String(app?.app_name || '').toLowerCase();
    return pkg.includes('suitestream') || name.includes('suitestream');
  };

  const handleCreateDestination = async (targetSubTab = 'Destination Setup') => {
    const token = localStorage.getItem('token');
    const trimmedName = String(newDestinationName || '').trim();
    const normalizedCountryCode = String(newDestinationCountryCode || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z]/g, '');

    if (!trimmedName) {
      setMessage('Destination name is required.');
      return;
    }
    if (!normalizedCountryCode || normalizedCountryCode.length !== 2) {
      setMessage('Country code must be a 2-letter value (for example: vn, nz).');
      return;
    }
    if (!token) {
      setMessage('Please log in again.');
      return;
    }

    setCreatingDestination(true);
    setMessage('');
    try {
      const res = await fetch('/api/sites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          siteType: 'destination',
          countryCode: normalizedCountryCode,
          destinationSlug: trimmedName,
          siteSlug: 'overview',
          hubStatus: 'unclassified',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to create destination (${res.status})`);
      }
      const newSiteId = data?.siteId;
      const newAreaId = data?.areaId;
      if (!newSiteId) {
        throw new Error('Destination was created but no site id was returned.');
      }
      if (refreshSitesAreasDevices) {
        await refreshSitesAreasDevices(newSiteId, newAreaId, true);
      }
      setNewDestinationName('');
      setNewDestinationCountryCode('');
      setSelectedSubTab(targetSubTab);
      setMessage(`Created destination "${trimmedName}".`);
    } catch (err) {
      setMessage(err.message || 'Failed to create destination.');
    } finally {
      setCreatingDestination(false);
    }
  };

  const renderCreateDestinationEmptyState = (
    targetSubTab = 'Destination Setup',
    emptyStateMessage = 'No destination exists yet. Create the first destination here.'
  ) => (
    <div className="rounded border border-dashed border-gray-700 bg-gray-900/40 p-4">
      <div className="text-sm text-gray-300">
        {emptyStateMessage}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
        <input
          className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white"
          placeholder="Destination name (e.g. Da Nang)"
          value={newDestinationName}
          onChange={(e) => setNewDestinationName(e.target.value)}
          disabled={creatingDestination}
        />
        <input
          className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white"
          placeholder="Country code (ISO-2, e.g. vn)"
          value={newDestinationCountryCode}
          onChange={(e) => setNewDestinationCountryCode(e.target.value)}
          disabled={creatingDestination}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className="settings-subtab-button active"
          onClick={() => handleCreateDestination(targetSubTab)}
          disabled={creatingDestination}
        >
          {creatingDestination ? 'Creating…' : 'Add Destination'}
        </button>
      </div>
    </div>
  );

  const openAppSelection = (deviceId) => {
    setAppSelectionDeviceId(deviceId);
    setAppSelectionApps([]);
    setAppSelectionEnabled(new Set());
    setAppSelectionError('');
    setAppSelectionStatus('loading');
    setShowAppSelectionModal(true);
  };

  useEffect(() => {
    let isMounted = true;
    const loadAnalytics = async () => {
      if (!site?.id) {
        setGaMeasurementId('');
        setFbPixelId('');
        setClarityProjectId('');
        setTiktokPixelId('');
        setGaSavedId('');
        setFbSavedId('');
        setClaritySavedId('');
        setTiktokSavedId('');
        setAnalyticsLoaded(false);
        setAnalyticsLoadError('');
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) return;
      setGaLoading(true);
      setFbLoading(true);
      setClarityLoading(true);
      setTiktokLoading(true);
      setAnalyticsLoadError('');
      try {
        const res = await fetch(`/api/sites/${site.id}/analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (res.status === 404) {
            if (!isMounted) return;
            setGaMeasurementId('');
            setFbPixelId('');
            setClarityProjectId('');
            setTiktokPixelId('');
            setGaSavedId('');
            setFbSavedId('');
            setClaritySavedId('');
            setTiktokSavedId('');
            setAnalyticsLoaded(true);
            return;
          }
          const message = res.status === 401 || res.status === 403
            ? 'Sign in to load analytics settings.'
            : 'Failed to load analytics settings.';
          throw new Error(message);
        }
        const data = await res.json();
        if (!isMounted) return;
        setGaMeasurementId(data?.gaMeasurementId || '');
        setFbPixelId(data?.fbPixelId || '');
        setClarityProjectId(data?.clarityProjectId || '');
        setTiktokPixelId(data?.tiktokPixelId || '');
        setGaSavedId(data?.gaMeasurementId || '');
        setFbSavedId(data?.fbPixelId || '');
        setClaritySavedId(data?.clarityProjectId || '');
        setTiktokSavedId(data?.tiktokPixelId || '');
        setAnalyticsLoaded(true);
      } catch (err) {
        if (!isMounted) return;
        setAnalyticsLoadError(err.message || 'Failed to load analytics settings.');
      } finally {
        if (isMounted) {
          setGaLoading(false);
          setFbLoading(false);
          setClarityLoading(false);
          setTiktokLoading(false);
        }
      }
    };

    loadAnalytics();
    return () => {
      isMounted = false;
    };
  }, [site?.id]);

  const saveAnalyticsSettings = async (siteId, nextGaId, nextFbId, nextClarityId, nextTiktokId) => {
    if (!siteId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setGaSaving(true);
    setFbSaving(true);
    setClaritySaving(true);
    setTiktokSaving(true);
    setAnalyticsSaveError('');
    try {
      const res = await fetch(`/api/sites/${siteId}/analytics`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          gaMeasurementId: nextGaId || null,
          fbPixelId: nextFbId || null,
          clarityProjectId: nextClarityId || null,
          tiktokPixelId: nextTiktokId || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to save analytics settings');
      const savedGa = data?.gaMeasurementId || '';
      const savedFb = data?.fbPixelId || '';
      const savedClarity = data?.clarityProjectId || '';
      const savedTiktok = data?.tiktokPixelId || '';
      setGaMeasurementId(savedGa);
      setFbPixelId(savedFb);
      setClarityProjectId(savedClarity);
      setTiktokPixelId(savedTiktok);
      setGaSavedId(savedGa);
      setFbSavedId(savedFb);
      setClaritySavedId(savedClarity);
      setTiktokSavedId(savedTiktok);
      setAnalyticsLoaded(true);
    } catch (err) {
      setAnalyticsSaveError(err.message || 'Failed to save analytics settings.');
    } finally {
      setGaSaving(false);
      setFbSaving(false);
      setClaritySaving(false);
      setTiktokSaving(false);
    }
  };

  const normalizedGaId = (gaMeasurementId || '').trim();
  const normalizedFbId = (fbPixelId || '').trim();
  const normalizedClarityId = (clarityProjectId || '').trim();
  const normalizedTiktokId = (tiktokPixelId || '').trim();
  const gaDirty = normalizedGaId !== (gaSavedId || '');
  const fbDirty = normalizedFbId !== (fbSavedId || '');
  const clarityDirty = normalizedClarityId !== (claritySavedId || '');
  const tiktokDirty = normalizedTiktokId !== (tiktokSavedId || '');
  const gaButtonLabel = gaSaving ? 'Saving...' : gaDirty ? 'Save GA4 ID' : analyticsLoaded ? 'Saved' : 'Save GA4 ID';
  const fbButtonLabel = fbSaving ? 'Saving...' : fbDirty ? 'Save Pixel ID' : analyticsLoaded ? 'Saved' : 'Save Pixel ID';
  const clarityButtonLabel = claritySaving ? 'Saving...' : clarityDirty ? 'Save Clarity ID' : analyticsLoaded ? 'Saved' : 'Save Clarity ID';
  const tiktokButtonLabel = tiktokSaving ? 'Saving...' : tiktokDirty ? 'Save TikTok ID' : analyticsLoaded ? 'Saved' : 'Save TikTok ID';

  const openDeviceEdit = (device) => {
    if (!device?.id) return;
    if (device.type === 'lgtv') { setLgEditDevice(device); return; }
    if (isLgPlayer(device)) { setPlayerEditDevice(device); return; }
    setDeviceEditDevice(device);
    setDeviceEditStartupMode(device.startup_mode || 'AUTO_START');
    setDeviceEditAllowLocalMenu(device.allow_local_menu == null ? true : Boolean(device.allow_local_menu));
    setDeviceEditEnsureBackgroundService(device.ensure_background_service === true || device.ensure_background_service === 1);
    setDeviceEditMenuPin(device.menu_pin || '');
    setDeviceEditApps([]);
    setDeviceEditAppsLoaded(false);
    setDeviceEditEnabled(new Set());
    setDeviceEditStatus('loading');
    setDeviceEditError('');
    setShowDeviceEditModal(true);
  };

  const closeDeviceEdit = () => {
    setShowDeviceEditModal(false);
    setDeviceEditDevice(null);
    setDeviceEditApps([]);
    setDeviceEditEnabled(new Set());
    setDeviceEditStatus('idle');
    setDeviceEditError('');
  };

  const openReconnectDevice = (device) => {
    setReconnectDevice(device);
    setReconnectCode('');
    setReconnectStatus('idle');
    setReconnectMessage('');
  };

  const closeReconnectDevice = () => {
    if (reconnectStatus === 'submitting') return;
    setReconnectDevice(null);
    setReconnectCode('');
    setReconnectStatus('idle');
    setReconnectMessage('');
  };

  const handleReconnectDevice = async () => {
    const code = reconnectCode.trim();
    if (!reconnectDevice?.id || !/^\d{6}$/.test(code)) {
      setReconnectStatus('error');
      setReconnectMessage('Enter the six-digit code shown on the device.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setReconnectStatus('error');
      setReconnectMessage('Please log in again.');
      return;
    }

    try {
      setReconnectStatus('submitting');
      setReconnectMessage('');
      const response = await fetch('/api/devices/reconnect-device', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          deviceId: reconnectDevice.id,
          verificationCode: code,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        setReconnectStatus('error');
        setReconnectMessage(result.message || 'Failed to reconnect device.');
        return;
      }
      setReconnectStatus('success');
      setReconnectMessage(result.message || 'Device reconnected.');
    } catch {
      setReconnectStatus('error');
      setReconnectMessage('Failed to reconnect device.');
    }
  };

  const closeAppSelection = () => {
    setShowAppSelectionModal(false);
    setAppSelectionDeviceId(null);
    setAppSelectionApps([]);
    setAppSelectionEnabled(new Set());
    setAppSelectionStatus('idle');
    setAppSelectionError('');
  };

  const sendDeviceCommand = (deviceId, command, value = null) => {
    if (!socket || !deviceId) return null;
    const id = commandIdRef.current++;
    socket.emit('sendCommand', { deviceId, command, value, id });
    return id;
  };

  useEffect(() => {
    if (selectedSubTab !== 'Devices' || !devices.length) {
      setKodiHosts([]);
      return;
    }
    const token = localStorage.getItem('token');
    const candidates = devices.filter((device) => device.is_app && device.id);
    if (!token || !candidates.length) {
      setKodiHosts([]);
      return;
    }
    let cancelled = false;
    const discoverKodiHosts = async () => {
      candidates.forEach((device) => sendDeviceCommand(device.id, 'list_apps'));
      await new Promise((resolve) => setTimeout(resolve, 1200));
      const results = await Promise.all(
        candidates.map(async (device) => {
          try {
            const response = await fetch(`/api/devices/get-installed-apps?deviceId=${device.id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            const hasKodi = response.ok
              && Array.isArray(data.apps)
              && data.apps.some((app) => app.package_name === 'org.xbmc.kodi' && app.launchable);
            return hasKodi ? device : null;
          } catch (error) {
            return null;
          }
        })
      );
      if (!cancelled) setKodiHosts(results.filter(Boolean));
    };
    discoverKodiHosts();
    return () => {
      cancelled = true;
    };
  }, [selectedSubTab, devices, socket]);

  useEffect(() => {
    if (activeAddPanel !== 'kodi' || !kodiHostDeviceId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const loadKodiConfig = async () => {
      setKodiStatus('');
      try {
        const response = await fetch(`/api/devices/kodi-config?deviceId=${kodiHostDeviceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (cancelled || !response.ok || !data.success) return;
        const config = data.config || {};
        setKodiHost(config.host || '127.0.0.1');
        setKodiPort(String(config.port || 8080));
        setKodiUsername(config.username || 'kodi');
        setKodiPassword(config.passwordConfigured ? '' : '9999');
        setKodiPasswordConfigured(config.passwordConfigured === true);
        setKodiAuthenticationEnabled(config.authenticationEnabled === true);
      } catch (error) {
        if (!cancelled) setKodiStatus('Could not load the saved Kodi configuration.');
      }
    };
    loadKodiConfig();
    return () => {
      cancelled = true;
    };
  }, [activeAddPanel, kodiHostDeviceId]);

  const buildKodiConfig = () => ({
    host: kodiHost.trim() || '127.0.0.1',
    port: Number(kodiPort || 8080),
    username: kodiUsername.trim() || 'kodi',
    password: kodiPassword,
    authenticationEnabled: kodiAuthenticationEnabled,
  });

  const saveKodiConfig = async () => {
    const token = localStorage.getItem('token');
    if (!token || !kodiHostDeviceId) return;
    setKodiBusy(true);
    setKodiStatus('');
    try {
      const response = await fetch('/api/devices/kodi-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deviceId: Number(kodiHostDeviceId), config: buildKodiConfig() }),
      });
      const data = await response.json();
      setKodiStatus(response.ok && data.success
        ? 'Kodi settings saved.'
        : data.message || 'Failed to save Kodi settings.');
    } catch (error) {
      setKodiStatus('Failed to save Kodi settings.');
    } finally {
      setKodiBusy(false);
    }
  };

  const testKodiConnection = async () => {
    const token = localStorage.getItem('token');
    if (!token || !kodiHostDeviceId) return;
    setKodiBusy(true);
    setKodiStatus('Testing Kodi connection…');
    try {
      const response = await fetch('/api/devices/kodi-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deviceId: Number(kodiHostDeviceId), config: buildKodiConfig() }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setKodiStatus(data.message || 'Kodi connection test failed.');
      } else if (data.active && data.identities?.length) {
        const identity = data.identities[0];
        setKodiStatus(`Connected. Playing: ${identity.title || identity.identity || 'unknown item'}`);
      } else {
        setKodiStatus('Connected to Kodi. No active player was found.');
      }
    } catch (error) {
      setKodiStatus('Kodi connection test failed.');
    } finally {
      setKodiBusy(false);
    }
  };

  const deviceEditSelectableApps = useMemo(() => {
    return (deviceEditApps || [])
      .filter((a) => !isSuitestreamApp(a))
      .filter((a) => a.launchable);
  }, [deviceEditApps]);

  useEffect(() => {
    if (!showDeviceEditModal || !deviceEditDevice?.id) return;
    if (deviceEditStatus === 'ready' || deviceEditStatus === 'saving') return;
    const token = localStorage.getItem('token');
    if (!token) return;

    let cancelled = false;
    let pollTimer = null;

    const fetchApps = async () => {
      try {
        const res = await fetch(`/api/devices/get-installed-apps?deviceId=${deviceEditDevice.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.success) return;
        const apps = Array.isArray(data.apps) ? data.apps : [];
        setDeviceEditAppsLoaded(true);
        setDeviceEditApps(apps);
        setDeviceEditEnabled(new Set(apps.filter((a) => a.enabled).map((a) => a.package_name)));
        setDeviceEditStatus('ready');
      } catch (err) {
        if (cancelled) return;
      }
    };

    sendDeviceCommand(deviceEditDevice.id, 'list_apps');
    fetchApps();
    pollTimer = setInterval(fetchApps, 1500);

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [showDeviceEditModal, deviceEditDevice, deviceEditStatus, socket]);

  useEffect(() => {
    if (!showAppSelectionModal || !appSelectionDeviceId) return;
    if (appSelectionStatus === 'ready' || appSelectionStatus === 'saving') return;
    const token = localStorage.getItem('token');
    if (!token) return;

    let cancelled = false;
    let pollTimer = null;

    const fetchApps = async () => {
      try {
        const res = await fetch(`/api/devices/get-installed-apps?deviceId=${appSelectionDeviceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.success) {
          return;
        }
        const apps = Array.isArray(data.apps) ? data.apps : [];
        if (!apps.length) return;

        setAppSelectionApps(apps);
        setAppSelectionEnabled(new Set(apps.filter((a) => a.enabled).map((a) => a.package_name)));
        setAppSelectionStatus('ready');
      } catch (err) {
        if (cancelled) return;
      }
    };

    // Trigger device to refresh its app list, then poll until list arrives.
    sendDeviceCommand(appSelectionDeviceId, 'list_apps');
    fetchApps();
    pollTimer = setInterval(fetchApps, 1500);

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [showAppSelectionModal, appSelectionDeviceId, appSelectionStatus, socket]);
  // Load devices in the selected area

  const canAddWithLimit = (limitKey) => {
    if (!limitKey) return true;
    const limit = limits?.limits?.[limitKey];
    if (limit === 'unlimited' || limit === null || limit === undefined) return true;
    const usedMap = limits?.used || {};

    const usedKey =
      limitKey === 'tv_screens_included'
        ? 'tv_screens'
        : limitKey === 'music_players'
          ? 'music_players'
          : limitKey === 'staff_phones'
            ? 'staff_phones'
            : limitKey === 'console_tablets'
              ? 'console_tablets'
              : null;

    const used = usedKey ? Number(usedMap[usedKey] || 0) : 0;
    const numericLimit = Number(limit);
    if (!Number.isFinite(numericLimit)) return true;
    return used < numericLimit;
  };

  const startAddPanelWithLimit = (panel, limitKey, label) => {
    if (!canAddWithLimit(limitKey)) {
      const limit = limits?.limits?.[limitKey];
      const usedMap = limits?.used || {};
      const usedKey =
        limitKey === 'tv_screens_included'
          ? 'tv_screens'
          : limitKey === 'music_players'
            ? 'music_players'
            : limitKey === 'staff_phones'
              ? 'staff_phones'
              : limitKey === 'console_tablets'
                ? 'console_tablets'
                : null;
      const used = usedKey ? Number(usedMap[usedKey] || 0) : 0;
      showNudge({
        title: 'Upgrade required',
        message: `You have reached your limit for ${label} (${used}/${limit}). Upgrade to add more.`,
      });
      return;
    }
    startAddPanel(panel);
  };

  useEffect(() => {
    if (!requestedSubTab) return;
    const normalizedRequestedSubTab =
      requestedSubTab === 'Events' || requestedSubTab === 'Plugins'
        ? 'Plugins'
        : requestedSubTab;
    if (isDestinationMode && normalizedRequestedSubTab === 'Site Details') {
      setSelectedSubTab(showDestinationSetup ? 'Destination Setup' : 'Shortcuts');
      return;
    }
    setSelectedSubTab(normalizedRequestedSubTab);
  }, [isDestinationMode, requestedSubTab, showDestinationSetup]);

  useEffect(() => {
    if (!requestedDeviceRequestKey || !requestedDeviceId || selectedSubTab !== 'Devices') return;
    if (handledDeviceRequestKeyRef.current === requestedDeviceRequestKey) return;
    const requestedDevice = devices.find(
      (entry) => String(entry.id) === String(requestedDeviceId)
    );
    if (requestedDevice) {
      handledDeviceRequestKeyRef.current = requestedDeviceRequestKey;
      openDeviceEdit(requestedDevice);
    }
  }, [devices, requestedDeviceId, requestedDeviceRequestKey, selectedSubTab]);

  useEffect(() => {
    if (selectedSubTab === 'Destination Setup' && !showDestinationSetup) {
      setSelectedSubTab(isDestinationMode ? 'Shortcuts' : 'Site Details');
    }
  }, [isDestinationMode, selectedSubTab, showDestinationSetup]);

  useEffect(() => {
    if (selectedSubTab === 'AI Agents' && !showAiAgentsTab) {
      setSelectedSubTab('Site Details');
    }
  }, [selectedSubTab, showAiAgentsTab]);

  useEffect(() => {
    if (selectedSubTab === 'Users' && !canManageAccountUsers) {
      setSelectedSubTab(isDestinationMode ? (showDestinationSetup ? 'Destination Setup' : 'Shortcuts') : 'Site Details');
    }
  }, [canManageAccountUsers, isDestinationMode, selectedSubTab, showDestinationSetup]);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(connectUrl, { errorCorrectionLevel: 'M', margin: 2, width: 180 })
      .then((dataUrl) => {
        if (!cancelled) setConnectQrDataUrl(dataUrl);
      })
      .catch((err) => {
        console.error('[Settings] Failed to generate connect QR code', err);
        if (!cancelled) setConnectQrDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [connectUrl]);

  useEffect(() => {
    if (!isDestinationMode) return;
    if (selectedSubTab === 'Devices' || selectedSubTab === 'Site Details') {
      setSelectedSubTab(showDestinationSetup ? 'Destination Setup' : 'Shortcuts');
    }
  }, [isDestinationMode, selectedSubTab, showDestinationSetup]);

  useEffect(() => {
    if (siteDetailsPlaceId) setOneTimeSiteDetailsPlaceId(siteDetailsPlaceId);
  }, [siteDetailsPlaceId]);

  useEffect(() => {
    if (selectedSubTab === 'Site Details' && oneTimeSiteDetailsPlaceId && site?.id) {
      setOneTimeSiteDetailsPlaceId(null);
      onConsumeSiteDetailsPlaceId?.();
    }
  }, [selectedSubTab, oneTimeSiteDetailsPlaceId, site?.id]);

  useEffect(() => {
    let ignore = false;
    const fetchDevices = async () => {
      if (!area) return;
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`/api/areas/${area.id}/devices`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!ignore && res.ok) {
          const data = await res.json();
          setDevices(data.devices || []);
          setIrBridges(data.irBridges || []);
        }
      } catch (err) {
        if (!ignore) console.error('Error fetching devices:', err);
      }
    };
    fetchDevices();
    return () => { ignore = true; };
  }, [area]);

  // Verify numeric code or IR bridge
  const handleVerifyDevice = async () => {
    const code = verificationCode.trim();
    if (!code) {
      setMessage('Please enter the verification code.');
      return;
    }
    if (!area) {
      setMessage('Please select an area.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in again.');
      return;
    }
    try {
      const res = await fetch('/api/devices/verify-device', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ code, browserDetails:{}, areaId:area.id })
      });
      const result = await res.json();
      if (res.ok && result.success) {
        setDeviceStatusMsg('Verified - please enter device details below');
        setShowDeviceDetailsForm(true);
        setVerifiedCode(code);
        setIsIRBridge(!/^\d{6}$/.test(code));
        setVerifiedClientIsApp(Boolean(result?.client?.isApp));
      } else {
        setMessage(result.message || 'Failed to verify device.');
      }
    } catch {
      alert('Error during verification.');
    }
  };

  // Save device (numeric, TV, or Source)
  const handleSaveDevice = async () => {
    if (!deviceName) {
      setMessage('Please enter device name.');
      return;
    }
    if ((addTVMode || addSourceMode) && !deviceIP.trim()) {
      setMessage('Please enter the device IP address.');
      return;
    }
    if (!area) {
      setMessage('Please select an area.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in again.');
      return;
    }

    try {
      let payload;
      const controller = devices.find(d => d.type && d.type.startsWith('controller'));
      if (addTVMode) {
        payload = { deviceName, deviceType:'lgtv', areaId:area.id, ip:deviceIP.trim() };
        if (controller) payload.controlledBy = controller.id;
      } else if (addSourceMode) {
        payload = { deviceName, deviceType:'stb', areaId:area.id, ip:deviceIP.trim() };
        if (controller) payload.controlledBy = controller.id;
      } else {
        if (!verifiedCode) {
          setMessage('Verification code missing or unverified.');
          return;
        }
        payload = { verificationCode:verifiedCode, deviceName, deviceType, areaId:area.id };

        const isScreenOrMusic = activeAddPanel === 'screen' || activeAddPanel === 'music';
        if (verifiedClientIsApp && isScreenOrMusic) {
          payload.startup_mode = startupMode;
          payload.allow_local_menu = allowLocalMenu;
          payload.menu_pin = menuPin;
        }
      }

      const res = await fetch('/api/devices/save-device', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body:JSON.stringify(payload)
      });
      const result = await res.json();

      if (res.ok && result.success) {
        // Reset form
        setOnboardingIP(deviceIP.trim());
        setDeviceName('');
        setDeviceIP('');
        setDeviceType('Music Player');
        setVerificationCode('');
        setVerifiedCode('');
        setShowDeviceDetailsForm(false);
        setActiveAddPanel(null);
        setTvControlDeviceType('');

        // Store new TV id
        const jwt = result.jwt;
        const tvId = JSON.parse(atob(jwt.split('.')[1])).deviceId;
        setNewDeviceId(tvId);

        if (addTVMode) {
          // Start onboarding wizard
          setOnboardingLogs([`🚀 Starting onboarding for ${deviceIP.trim()}…`]);
          setOnboardingStep('initial');
          setShowOnboardingModal(true);
          refreshEntitlements();

          if (!controller) {
            setOnboardingLogs(l => [
              ...l,
              '❌ No controller agent connected; cannot run ADB.'
            ]);
            setOnboardingStep('error');
            return;
          }
          // Kick off step 1
          runInitialConnect(controller.id, deviceIP.trim(), tvId);
          return;
        } else {
          // Non-TV devices
          const isScreenOrMusic = activeAddPanel === 'screen' || activeAddPanel === 'music';
          if (verifiedClientIsApp && isScreenOrMusic) {
            openAppSelection(tvId);
          } else {
            alert('Device registered successfully! Press play on the device to get started');
          }
          refreshSitesAreasDevices(site.id, area?.id, true, tvId);
          refreshEntitlements();
        }

        setAddTVMode(false);
        setAddSourceMode(false);
      } else {
        setMessage(result.message || 'Failed to register device.');
      }
    } catch (err) {
      alert('Error while saving device: ' + err.message);
    }
  };

  const resetAddFlow = () => {
    setMessage('');
    setVerificationCode('');
    setVerifiedCode('');
    setDeviceStatusMsg('');
    setDeviceName('');
    setDeviceIP('');
    setShowDeviceDetailsForm(false);
    setAddTVMode(false);
    setAddSourceMode(false);
    setIsIRBridge(false);
    setVerifiedClientIsApp(false);
    setStartupMode('AUTO_START');
    setAllowLocalMenu(true);
    setMenuPin('');
    setKodiHostDeviceId('');
    setKodiHost('127.0.0.1');
    setKodiPort('8080');
    setKodiUsername('kodi');
    setKodiPassword('9999');
    setKodiPasswordConfigured(false);
    setKodiAuthenticationEnabled(false);
    setKodiStatus('');
    setKodiBusy(false);
  };

  const startAddPanel = (panel) => {
    resetAddFlow();
    setActiveAddPanel(panel);
    setTvControlDeviceType('');

    if (panel === 'screen') setDeviceType('TV');
    if (panel === 'music') setDeviceType('Music Player');
    if (panel === 'staff') setDeviceType('Staff');
    if (panel === 'console') setDeviceType('Stage Screen');
    if (panel === 'tvControlDevice') setDeviceType('');
    if (panel === 'controlTv') setAddSourceMode(true);
    if (panel === 'kodi') setKodiHostDeviceId(String(kodiHosts[0]?.id || ''));
  };

  const leaveAddPanel = () => {
    resetAddFlow();
    setActiveAddPanel(null);
    setTvControlDeviceType('');
  };

  const handleSetupRemotes = (id) => {
    alert(`Setup Remotes for device ${id} (to be implemented)`);
  };

  const handleForgetDevice = async (id, isEphemeral) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in again.');
      return;
    }
    try {
      const res = await fetch('/api/devices/forget-device', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body:JSON.stringify({ deviceId:id, isEphemeral })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (isEphemeral) {
          setIrBridges(bs => bs.filter(b => b.id !== id));
        } else {
          setDevices(ds => ds.filter(d => d.id !== id));
        }
        await refreshSitesAreasDevices(site.id, area ? area.id : undefined, true);
        alert('Device forgotten. Its Screen connections and saved settings have been updated.');
      } else {
        alert(`Forget failed: ${data.message || 'unknown'}`);
      }
    } catch {
      alert('Error forgetting device.');
    }
  };

  // Legacy inline startup editor removed in favour of the full Edit modal.


  //
  // ——— Onboarding helper functions ———
  //

  // Step 1: adb connect
  const runInitialConnect = async (controllerId, ip, tvDeviceId) => {
    const cmd = `adb connect ${ip}:5555`;
    setOnboardingLogs(l => [...l, `📡 ${cmd}`]);
    const res = await fetch('/controllers/api/command', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId: controllerId, command: cmd })
    });
    const outObj = await res.json();
    const out = (outObj.stdout || outObj.stderr || outObj.error || '').trim();
    setOnboardingLogs(l => [...l, out]);

    runDevicesCheck(controllerId, ip, tvDeviceId);
  };

  // Step 2: adb devices & next step
  const runDevicesCheck = async (controllerId, ip, tvDeviceId) => {
    const listRes = await fetch('/controllers/api/command', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId: controllerId, command: 'adb devices' })
    });
    const listObj = await listRes.json();
    const lines = (listObj.stdout || '').split('\n').map(l => l.trim());
    const entry = lines.find(l => l.startsWith(ip));
    if (entry?.endsWith('device')) {
      setOnboardingStep('success');
      return;
    }
    if (entry?.endsWith('unauthorized')) {
      setOnboardingStep('prompt');
      return;
    }
    if ((listObj.error || '').toLowerCase().includes('refused')) {
      setOnboardingStep('debug');
      return;
    }
    setOnboardingStep('diagnose');
  };

  // Cancel/on abort
  const cancelOnboarding = () => {
    if (newDeviceId) {
      handleForgetDevice(newDeviceId, false);
    }
    setShowOnboardingModal(false);
    setOnboardingStep(null);
    setOnboardingLogs([]);
  };

  return (
    <div className="settings">
      <div className="settings-subtabs">
        <button
          type="button"
          className={`settings-subtab-button ${selectedSubTab === 'Shortcuts' ? 'active' : ''}`}
          onClick={() => setSelectedSubTab('Shortcuts')}
        >
          Shortcuts
        </button>
        {!isDestinationMode && (
          <button
            type="button"
            className={`settings-subtab-button ${selectedSubTab === 'Devices' ? 'active' : ''}`}
            onClick={() => setSelectedSubTab('Devices')}
          >
            Devices
          </button>
        )}
        <button
          type="button"
          className={`settings-subtab-button ${selectedSubTab === 'Integrations' ? 'active' : ''}`}
          onClick={() => setSelectedSubTab('Integrations')}
        >
          Integrations
        </button>
        {!isDestinationMode && (
          <button
            type="button"
            className={`settings-subtab-button ${selectedSubTab === 'Site Details' ? 'active' : ''}`}
            onClick={() => setSelectedSubTab('Site Details')}
          >
            {detailsTabLabel}
          </button>
        )}
        {canManageAccountUsers && (
          <button
            type="button"
            className={`settings-subtab-button ${selectedSubTab === 'Users' ? 'active' : ''}`}
            onClick={() => setSelectedSubTab('Users')}
          >
            Users
          </button>
        )}
        {showDestinationSetup && (
          <button
            type="button"
            className={`settings-subtab-button ${selectedSubTab === 'Destination Setup' ? 'active' : ''}`}
            onClick={() => setSelectedSubTab('Destination Setup')}
          >
            Destination Setup
          </button>
        )}
        <button
          type="button"
          className={`settings-subtab-button ${selectedSubTab === 'My Subscriptions' ? 'active' : ''}`}
          onClick={() => setSelectedSubTab('My Subscriptions')}
        >
          My Subscriptions
        </button>
        <button
          type="button"
          className={`settings-subtab-button ${selectedSubTab === 'Site Maps' ? 'active' : ''}`}
          onClick={() => setSelectedSubTab('Site Maps')}
        >
          {mapsTabLabel}
        </button>
        <button
          type="button"
          className={`settings-subtab-button ${selectedSubTab === 'Watermark' ? 'active' : ''}`}
          onClick={() => setSelectedSubTab('Watermark')}
        >
          Watermark
        </button>
        <button
          type="button"
          className={`settings-subtab-button ${selectedSubTab === 'Wi-Fi Settings' ? 'active' : ''}`}
          onClick={() => setSelectedSubTab('Wi-Fi Settings')}
        >
          Wi-Fi Settings
        </button>
        <button
          type="button"
          className={`settings-subtab-button ${selectedSubTab === 'Default Ads' ? 'active' : ''}`}
          onClick={() => setSelectedSubTab('Default Ads')}
        >
          Default Ads
        </button>
        <button
          type="button"
          className={`settings-subtab-button ${selectedSubTab === 'Plugins' ? 'active' : ''}`}
          onClick={() => setSelectedSubTab('Plugins')}
        >
          Plugins
        </button>
        {showAiAgentsTab && (
          <button
            type="button"
            className={`settings-subtab-button ${selectedSubTab === 'AI Agents' ? 'active' : ''}`}
            onClick={() => setSelectedSubTab('AI Agents')}
          >
            AI Agents
          </button>
        )}
      </div>

      {selectedSubTab === 'Shortcuts' && (
        <>
          <ShortcutsSettings />
        </>
      )}

      {!isDestinationMode && selectedSubTab === 'Devices' && (
        <>
          {/* Register Device */}
          <div className="register-device">
            <h3 className="settings-section-title">Register a New Device</h3>

            {!activeAddPanel && (
              <>
                <div className="device-add-grid">
                  <button type="button" className="device-add-card" onClick={() => startAddPanelWithLimit('screen', 'tv_screens_included', 'TV screens')}>
                    <div className="device-add-title">🖥️ Add a Screen</div>
                    <div className="device-add-subtitle">Connect the Suitestream app or any web browser to your account</div>
                  </button>
                  <button type="button" className="device-add-card" onClick={() => startAddPanelWithLimit('music', 'music_players', 'music players')}>
                    <div className="device-add-title">🎶 Add a Background Music System</div>
                    <div className="device-add-subtitle">Connect the laptop, phone, tv or tablet connected to your venue soundsystem</div>
                  </button>
                  <button type="button" className="device-add-card" onClick={() => startAddPanel('controlTv')}>
                    <div className="device-add-title">📺 Control a TV</div>
                    <div className="device-add-subtitle">Control any Android TV with Google Assistant</div>
                  </button>
                  <button type="button" className="device-add-card" onClick={() => startAddPanel('tvControlDevice')}>
                    <div className="device-add-title">🛠️ Add a TV Control Device</div>
                    <div className="device-add-subtitle">Suitestream Nano or Raspberry Pi</div>
                  </button>
                  <button type="button" className="device-add-card" onClick={() => startAddPanelWithLimit('staff', 'staff_phones', 'phone remotes')}>
                    <div className="device-add-title">📱 Add a Phone Remote</div>
                    <div className="device-add-subtitle">Grant a staff member persistent remote control</div>
                  </button>
                  <button type="button" className="device-add-card" onClick={() => startAddPanelWithLimit('console', 'console_tablets', 'console remotes')}>
                    <div className="device-add-title">🎛️ Add a Console Remote</div>
                    <div className="device-add-subtitle">Connect a tablet to control the full venue from one surface</div>
                  </button>
                  {kodiHosts.length > 0 && (
                    <button type="button" className="device-add-card" onClick={() => startAddPanel('kodi')}>
                      <div className="device-add-title">▶️ Configure Kodi</div>
                      <div className="device-add-subtitle">Connect to Kodi running on a Suitestream Android device</div>
                    </button>
                  )}
                </div>
                {message && <p className="message">{message}</p>}
              </>
            )}

            {activeAddPanel && (
              <>
                <div className="device-add-flow-header">
                  <button type="button" className="device-add-back" onClick={leaveAddPanel}>
                    ← Back
                  </button>
                  <div className="device-add-flow-title">
                    {activeAddPanel === 'screen' && 'Add a Screen'}
                    {activeAddPanel === 'music' && 'Add a Background Music System'}
                    {activeAddPanel === 'controlTv' && 'Control a TV'}
                    {activeAddPanel === 'tvControlDevice' && 'Add a TV Control Device'}
                    {activeAddPanel === 'staff' && 'Add a Phone Remote'}
                    {activeAddPanel === 'console' && 'Add a Console Remote'}
                    {activeAddPanel === 'kodi' && 'Configure Kodi'}
                  </div>
                </div>

                {activeAddPanel === 'tvControlDevice' && !tvControlDeviceType && (
                  <div className="device-add-grid device-add-grid-2">
                    <button
                      type="button"
                      className="device-add-card"
                      onClick={() => {
                        setTvControlDeviceType('controller-nano');
                        setDeviceType('controller-nano');
                      }}
                    >
                      <div className="device-add-title">🧩 Suitestream Nano (BLE, IR, HDMI-CEC, USB)</div>
                      <div className="device-add-subtitle">Controller device</div>
                    </button>
                    <button
                      type="button"
                      className="device-add-card"
                      onClick={() => {
                        setTvControlDeviceType('controller-rpi');
                        setDeviceType('controller-rpi');
                      }}
                    >
                      <div className="device-add-title">🍓 Raspberry Pi (ADB Network Control)</div>
                      <div className="device-add-subtitle">Controller device</div>
                    </button>
                  </div>
                )}

                {activeAddPanel === 'controlTv' && (
                  <>
                    <p className="message">Enter name and IP for the TV (must match Google Home)</p>
                    <input
                      type="text"
                      value={deviceName}
                      onChange={e => setDeviceName(e.target.value)}
                      placeholder="TV Name"
                      required
                    />
                    <input
                      type="text"
                      value={deviceIP}
                      onChange={e => setDeviceIP(e.target.value)}
                      placeholder="Device IP address"
                      required
                    />
                    <button onClick={handleSaveDevice}>Save Device</button>
                    {message && <p className="message">{message}</p>}
                  </>
                )}

                {activeAddPanel === 'kodi' && (
                  <div className="device-add-startup-options">
                    <p className="message">
                      Kodi was detected on the selected Suitestream device. The default host is loopback because Kodi normally runs on the same box.
                    </p>
                    <label htmlFor="kodiHostDevice">Suitestream device</label>
                    <select
                      id="kodiHostDevice"
                      value={kodiHostDeviceId}
                      onChange={(event) => setKodiHostDeviceId(event.target.value)}
                    >
                      {kodiHosts.map((device) => (
                        <option key={device.id} value={device.id}>{device.name || `Device ${device.id}`}</option>
                      ))}
                    </select>

                    <label htmlFor="kodiHost">Kodi host</label>
                    <input
                      id="kodiHost"
                      type="text"
                      value={kodiHost}
                      onChange={(event) => setKodiHost(event.target.value)}
                      placeholder="127.0.0.1"
                    />

                    <label htmlFor="kodiPort">Port</label>
                    <input
                      id="kodiPort"
                      type="number"
                      min="1"
                      max="65535"
                      value={kodiPort}
                      onChange={(event) => setKodiPort(event.target.value)}
                      placeholder="8080"
                    />

                    <label htmlFor="kodiAuthenticationEnabled" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        id="kodiAuthenticationEnabled"
                        type="checkbox"
                        checked={kodiAuthenticationEnabled}
                        onChange={(event) => setKodiAuthenticationEnabled(event.target.checked)}
                      />
                      Kodi web interface requires authentication
                    </label>

                    {kodiAuthenticationEnabled && (
                      <>
                        <label htmlFor="kodiUsername">Username</label>
                        <input
                          id="kodiUsername"
                          type="text"
                          autoComplete="username"
                          value={kodiUsername}
                          onChange={(event) => setKodiUsername(event.target.value)}
                          placeholder="kodi"
                        />
                        <label htmlFor="kodiPassword">Password</label>
                        <input
                          id="kodiPassword"
                          type="password"
                          autoComplete="current-password"
                          value={kodiPassword}
                          onChange={(event) => {
                            setKodiPassword(event.target.value);
                            if (event.target.value) setKodiPasswordConfigured(false);
                          }}
                          placeholder={kodiPasswordConfigured ? 'Saved password (leave blank to keep)' : '9999'}
                        />
                      </>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" onClick={testKodiConnection} disabled={kodiBusy || !kodiHostDeviceId}>
                        {kodiBusy ? 'Working…' : 'Test Connection'}
                      </button>
                      <button type="button" onClick={saveKodiConfig} disabled={kodiBusy || !kodiHostDeviceId}>
                        Save Kodi Settings
                      </button>
                    </div>
                    {kodiStatus && <p className="message">{kodiStatus}</p>}
                  </div>
                )}

                {activeAddPanel !== 'controlTv' && activeAddPanel !== 'kodi' && (activeAddPanel !== 'tvControlDevice' || tvControlDeviceType) && (
                  <>
                    {!showDeviceDetailsForm && (
                      <>
                        {activeAddPanel !== 'tvControlDevice' && (
                          <div className="device-connect-qr-card">
                            {connectQrDataUrl ? (
                              <img
                                className="device-connect-qr"
                                src={connectQrDataUrl}
                                alt={`QR code for ${connectUrl}`}
                              />
                            ) : null}
                            <div className="device-connect-qr-copy">
                              <h6 className="device-add-helper">
                                Scan to open device onboarding
                              </h6>
                              <a href={connectUrl} target="_blank" rel="noreferrer">{connectUrl}</a>
                            </div>
                          </div>
                        )}
                        <input
                          type="text"
                          value={verificationCode}
                          onChange={e => setVerificationCode(e.target.value)}
                          placeholder="Verification Code"
                          required
                        />
                        <button onClick={handleVerifyDevice}>Verify Code</button>
                        {message && <p className="message">{message}</p>}
                      </>
                    )}

                    {showDeviceDetailsForm && isIRBridge && (
                      <>
                        <p className="message">IR Bridge verified. It will appear in the list below.</p>
                        <button type="button" onClick={leaveAddPanel}>Done</button>
                      </>
                    )}

                    {showDeviceDetailsForm && !isIRBridge && (
                      <>
                        <p className="message">{deviceStatusMsg}</p>
                        <input
                          type="text"
                          value={deviceName}
                          onChange={e => setDeviceName(e.target.value)}
                          placeholder="Device Name"
                          required
                        />
                        {verifiedClientIsApp && (activeAddPanel === 'screen' || activeAddPanel === 'music') && (
                          <div className="device-add-startup-options">
                            <p className="message">The background control agent starts in every mode. This setting controls the visible app after boot.</p>
                            <label htmlFor="startupMode">Startup Mode</label>
                            <select
                              id="startupMode"
                              value={startupMode}
                              onChange={e => setStartupMode(e.target.value)}
                            >
                              <option value="NORMAL">NORMAL – agent only; launch display manually</option>
                              <option value="AUTO_START">AUTO_START – agent and display on boot</option>
                              <option value="LAUNCHER">LAUNCHER – auto-start and become Home app</option>
                            </select>

                            <label htmlFor="allowLocalMenu" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                id="allowLocalMenu"
                                type="checkbox"
                                checked={allowLocalMenu}
                                onChange={e => setAllowLocalMenu(e.target.checked)}
                              />
                              Allow hidden local admin menu (Back x7)
                            </label>

                            <label htmlFor="menuPin">Menu PIN (optional)</label>
                            <input
                              id="menuPin"
                              type="password"
                              inputMode="numeric"
                              value={menuPin}
                              onChange={e => setMenuPin(e.target.value)}
                              placeholder="4 digits (leave blank for none)"
                            />
                          </div>
                        )}
                        <button onClick={handleSaveDevice}>Save Device</button>
                        {message && <p className="message">{message}</p>}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Device List */}
          <div className="device-list">
            <h3 className="settings-section-title">Devices in Selected Area</h3>
            {devices.length > 0 ? (
              <ul>
                {visiblePhysicalDevices(devices).map(d => (
                  <li key={d.id}>
                    <div className="device-list-main">
                      <div className="device-list-title">
                        {d.name || 'Unnamed'} <span className="device-list-sep">–</span> {d.type}
                      </div>
                      <div className="device-list-meta">
                        {d.is_app && (d.type === 'TV' || d.type === 'Music Player') && (
                          <>Startup: {d.startup_mode || 'AUTO_START'} · </>
                        )}
                        DeviceID: {d.id}
                      </div>
                    </div>

                    <div className="device-list-actions">
                      <button type="button" className="device-action secondary" onClick={() => openReconnectDevice(d)}>
                        Reconnect Device
                      </button>
                      {(d.type === 'lgtv' || isLgPlayer(d) || (d.is_app && (d.type === 'TV' || d.type === 'Music Player'))) && (
                        <button type="button" className="device-action secondary" onClick={() => openDeviceEdit(d)}>
                          Edit Device
                        </button>
                      )}
                      <button type="button" className="device-action danger" onClick={() => handleForgetDevice(d.id, false)}>
                        Forget
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No devices in this area.</p>
            )}

            {irBridges.length > 0 && (
              <>
                <h3>IR Bridges</h3>
                <ul>
                  {irBridges.map(b => (
                    <li key={b.id}>
                      <div>Hostname: {b.hostname}.local</div>
                      <div>Emitters: {b.ir_emitters}</div>
                      <button onClick={() => handleSetupRemotes(b.id)}>Setup Remotes</button>
                      <button onClick={() => handleForgetDevice(b.id, true)}>Forget Bridge</button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}

      {selectedSubTab === 'Integrations' && (
        <>
          <SocialIntegrations site={site} />
          <div className="canva-integration">
            <h3>Google Drive</h3>
            {googleDriveStatus.connected ? (
              <button style={{ backgroundColor: '#dc3545' }} onClick={disconnectGoogleDrive} disabled={!site?.id}>
                Disconnect from Google Drive
              </button>
            ) : (
              <button style={{ backgroundColor: '#28a745' }} onClick={connectGoogleDrive} disabled={!site?.id}>
                Connect Google Drive
              </button>
            )}
            {googleDriveStatus.needs_reauth && (
              <p style={{ marginTop: 8, color: '#ffc107' }}>Google Drive needs re-authorization.</p>
            )}
          </div>
          <div className="canva-integration">
            <h3>YouTube</h3>
            {youtubeStatus.connected ? (
              <button style={{ backgroundColor: '#dc3545' }} onClick={disconnectYouTube} disabled={!site?.id}>
                Disconnect from YouTube
              </button>
            ) : (
              <button style={{ backgroundColor: '#28a745' }} onClick={connectYouTube} disabled={!site?.id}>
                Connect YouTube
              </button>
            )}
            {youtubeStatus.needs_reauth && (
              <p style={{ marginTop: 8, color: '#ffc107' }}>YouTube needs re-authorization.</p>
            )}
          </div>
          <div className="canva-integration ga4-integration">
            <h3>Google Analytics</h3>
            <p>
              Platform analytics ID:{' '}
              {platformGa4Id ? <strong>{platformGa4Id}</strong> : <span>Not configured</span>}
            </p>
            {entitlements?.custom_ga4_enabled ? (
              <>
                <label htmlFor="ga4Id">Custom GA4 Measurement ID</label>
                <input
                  id="ga4Id"
                  type="text"
                  value={gaMeasurementId || ''}
                  onChange={(e) => {
                    setGaMeasurementId(e.target.value);
                    if (analyticsSaveError) setAnalyticsSaveError('');
                  }}
                  placeholder="G-XXXXXXXXXX"
                  disabled={!site?.id || gaSaving}
                />
                <button
                  type="button"
                  onClick={() =>
                    saveAnalyticsSettings(site.id, normalizedGaId, normalizedFbId, normalizedClarityId, normalizedTiktokId)
                  }
                  disabled={!site?.id || gaSaving || gaLoading || fbSaving || claritySaving || tiktokSaving || !gaDirty}
                >
                  {gaButtonLabel}
                </button>
              </>
            ) : (
              <>
                <label htmlFor="ga4Id">Custom GA4 Measurement ID</label>
                <input
                  id="ga4Id"
                  type="text"
                  value={gaMeasurementId || ''}
                  placeholder="G-XXXXXXXXXX"
                  disabled
                  readOnly
                />
                <button
                  type="button"
                  onClick={() =>
                    showNudge({
                      title: 'Upgrade required',
                      message: 'Custom analytics requires Standard or Premium.',
                    })
                  }
                  disabled={!site?.id}
                >
                  Custom analytics (upgrade to unlock)
                </button>
              </>
            )}
            {(gaLoading || fbLoading || clarityLoading || tiktokLoading) && (
              <p style={{ marginTop: 8 }}>Loading analytics settings...</p>
            )}
            {analyticsLoadError && <p style={{ marginTop: 8, color: '#dc3545' }}>{analyticsLoadError}</p>}
            {analyticsSaveError && <p style={{ marginTop: 8, color: '#dc3545' }}>{analyticsSaveError}</p>}
          </div>
          <div className="canva-integration fb-pixel-integration">
            <h3>Facebook Pixel</h3>
            <p>
              Platform pixel ID:{' '}
              {platformFbPixelId ? <strong>{platformFbPixelId}</strong> : <span>Not configured</span>}
            </p>
            {entitlements?.custom_fb_pixel_enabled ? (
              <>
                <label htmlFor="fbPixelId">Custom Facebook Pixel ID</label>
                <input
                  id="fbPixelId"
                  type="text"
                  value={fbPixelId || ''}
                  onChange={(e) => {
                    setFbPixelId(e.target.value);
                    if (analyticsSaveError) setAnalyticsSaveError('');
                  }}
                  placeholder="1234567890"
                  disabled={!site?.id || fbSaving}
                />
                <button
                  type="button"
                  onClick={() =>
                    saveAnalyticsSettings(site.id, normalizedGaId, normalizedFbId, normalizedClarityId, normalizedTiktokId)
                  }
                  disabled={!site?.id || fbSaving || fbLoading || gaSaving || claritySaving || tiktokSaving || !fbDirty}
                >
                  {fbButtonLabel}
                </button>
              </>
            ) : (
              <>
                <label htmlFor="fbPixelId">Custom Facebook Pixel ID</label>
                <input
                  id="fbPixelId"
                  type="text"
                  value={fbPixelId || ''}
                  placeholder="1234567890"
                  disabled
                  readOnly
                />
                <button
                  type="button"
                  onClick={() =>
                    showNudge({
                      title: 'Upgrade required',
                      message: 'Custom analytics requires Standard or Premium.',
                    })
                  }
                  disabled={!site?.id}
                >
                  Custom analytics (upgrade to unlock)
                </button>
              </>
            )}
          </div>
          <div className="canva-integration clarity-integration">
            <h3>Microsoft Clarity</h3>
            <p>
              Platform project ID:{' '}
              {platformClarityId ? <strong>{platformClarityId}</strong> : <span>Not configured</span>}
            </p>
            {entitlements?.custom_clarity_enabled ? (
              <>
                <label htmlFor="clarityProjectId">Custom Clarity Project ID</label>
                <input
                  id="clarityProjectId"
                  type="text"
                  value={clarityProjectId || ''}
                  onChange={(e) => {
                    setClarityProjectId(e.target.value);
                    if (analyticsSaveError) setAnalyticsSaveError('');
                  }}
                  placeholder="abcdefghij"
                  disabled={!site?.id || claritySaving}
                />
                <button
                  type="button"
                  onClick={() =>
                    saveAnalyticsSettings(site.id, normalizedGaId, normalizedFbId, normalizedClarityId, normalizedTiktokId)
                  }
                  disabled={!site?.id || claritySaving || clarityLoading || gaSaving || fbSaving || tiktokSaving || !clarityDirty}
                >
                  {clarityButtonLabel}
                </button>
              </>
            ) : (
              <>
                <label htmlFor="clarityProjectId">Custom Clarity Project ID</label>
                <input
                  id="clarityProjectId"
                  type="text"
                  value={clarityProjectId || ''}
                  placeholder="abcdefghij"
                  disabled
                  readOnly
                />
                <button
                  type="button"
                  onClick={() =>
                    showNudge({
                      title: 'Upgrade required',
                      message: 'Custom analytics requires Standard or Premium.',
                    })
                  }
                  disabled={!site?.id}
                >
                  Custom analytics (upgrade to unlock)
                </button>
              </>
            )}
          </div>
          <div className="canva-integration tiktok-integration">
            <h3>TikTok Pixel</h3>
            <p>
              Platform pixel ID:{' '}
              {platformTiktokPixelId ? <strong>{platformTiktokPixelId}</strong> : <span>Not configured</span>}
            </p>
            {entitlements?.custom_tiktok_pixel_enabled ? (
              <>
                <label htmlFor="tiktokPixelId">Custom TikTok Pixel ID</label>
                <input
                  id="tiktokPixelId"
                  type="text"
                  value={tiktokPixelId || ''}
                  onChange={(e) => {
                    setTiktokPixelId(e.target.value);
                    if (analyticsSaveError) setAnalyticsSaveError('');
                  }}
                  placeholder="C0ABCDEF1234567890"
                  disabled={!site?.id || tiktokSaving}
                />
                <button
                  type="button"
                  onClick={() =>
                    saveAnalyticsSettings(site.id, normalizedGaId, normalizedFbId, normalizedClarityId, normalizedTiktokId)
                  }
                  disabled={!site?.id || tiktokSaving || tiktokLoading || gaSaving || fbSaving || claritySaving || !tiktokDirty}
                >
                  {tiktokButtonLabel}
                </button>
              </>
            ) : (
              <>
                <label htmlFor="tiktokPixelId">Custom TikTok Pixel ID</label>
                <input
                  id="tiktokPixelId"
                  type="text"
                  value={tiktokPixelId || ''}
                  placeholder="C0ABCDEF1234567890"
                  disabled
                  readOnly
                />
                <button
                  type="button"
                  onClick={() =>
                    showNudge({
                      title: 'Upgrade required',
                      message: 'Custom analytics requires Standard or Premium.',
                    })
                  }
                  disabled={!site?.id}
                >
                  Custom analytics (upgrade to unlock)
                </button>
              </>
            )}
          </div>
          {isAdmin && (
            <div className="canva-integration">
              <h3>Travel Monetisation</h3>
              <p>Travelpayouts and GetYourGuide scripts are now managed in the Admin tab under <strong>Site Integrations</strong>.</p>
              <p>Landing pages and websites use those platform settings automatically when monetisation is enabled.</p>
            </div>
          )}
          <div className="canva-integration">
            <h3>Canva</h3>
            {canvaConnected ? (
              <button style={{ backgroundColor: '#dc3545' }} onClick={disconnectCanva}>
                Disconnect from Canva
              </button>
            ) : (
              <button style={{ backgroundColor: '#28a745' }} onClick={connectCanva}>
                Connect to Canva
              </button>
            )}
          </div>
          <div className="connected-integrations">
            <h3>Connected Services</h3>
            <div className="connected-integrations-list">
              {youtubeStatus.connected ? (
                <div className="connected-integrations-item">
                  <span>YouTube</span>
                  <button type="button" onClick={disconnectYouTube} disabled={!site?.id}>
                    Disconnect
                  </button>
                </div>
              ) : (
                <div className="connected-integrations-empty">No YouTube connection.</div>
              )}
            </div>
          </div>
          <div className="integrations-actions">
            <h3>Integrations</h3>
            <button type="button" onClick={() => {}}>
              Connect to Google Maps...
            </button>
            <button type="button" onClick={() => {}}>
              Connect to Spotify...
            </button>
            <button type="button" onClick={() => {}}>
              Connect to Twitch...
            </button>
            <button type="button" onClick={() => {}}>
              Add a Radio Stream...
            </button>
          </div>
          {!isDestinationMode && (
            <div style={{ marginTop: 20 }}>
              <GoogleHomeDevices
                currentDevice={currentDevice}
                currentArea={area}
                assistantLoginTrigger={assistantLoginTrigger}
              />
            </div>
          )}
        </>
      )}

      {!isDestinationMode && selectedSubTab === 'Site Details' && (
        <>
          {site ? (
            <ConfirmSite
              siteId={site.id}
              area={area}
              sharedDestinations={sharedDestinations}
              entityMode={entityMode}
              refreshSitesAreasDevices={refreshSitesAreasDevices}
              placeId={oneTimeSiteDetailsPlaceId || 'existing'}
              isAdmin={isAdmin}
              onOpenDestinationSetup={() => setSelectedSubTab('Destination Setup')}
            />
          ) : (
            <>
              <p>{`Select a ${siteLabel.toLowerCase()} to view ${detailsTabLabel.toLowerCase()}.`}</p>
              {isDestinationMode && renderCreateDestinationEmptyState('Destination Setup')}
            </>
          )}
        </>
      )}

      {selectedSubTab === 'Users' && canManageAccountUsers && (
        <UsersSettings />
      )}

      {selectedSubTab === 'My Subscriptions' && (
        <MySubscriptionSettings refreshEntitlements={refreshEntitlements} />
      )}

      {selectedSubTab === 'Destination Setup' && showDestinationSetup && (
        <>
          {site ? (
            <DestinationSetup
              site={site || null}
              refreshSitesAreasDevices={refreshSitesAreasDevices}
              onUpdateSiteFields={onUpdateSiteFields}
            />
          ) : (
            renderCreateDestinationEmptyState('Destination Setup')
          )}
        </>
      )}

      {selectedSubTab === 'Site Maps' && (
        <>
          {site ? (
            <ConfirmSite
              siteId={site.id}
              area={area}
              sharedDestinations={sharedDestinations}
              entityMode={entityMode}
              refreshSitesAreasDevices={refreshSitesAreasDevices}
              placeId={oneTimeSiteDetailsPlaceId || 'existing'}
              mode="maps"
            />
          ) : (
            <p>{`Select a ${siteLabel.toLowerCase()} to view ${mapsTabLabel.toLowerCase()}.`}</p>
          )}
        </>
      )}

      {selectedSubTab === 'Watermark' && (
        <>
          {site ? (
            <WatermarkSettings
              site={site}
              currentDevice={currentDevice}
              subscriptionType={subscriptionType}
            />
          ) : (
            <p>Select a site to configure the watermark.</p>
          )}
        </>
      )}

      {selectedSubTab === 'Wi-Fi Settings' && (
        <>
          <WifiSettings site={site || null} />
        </>
      )}

      {selectedSubTab === 'Default Ads' && (
        <>
          <WifiTab
            siteId={site?.id || currentDevice?.site_id || currentDevice?.siteId || null}
            site={site || null}
            selectedDevice={currentDevice || null}
            onGoToSiteDetails={() => setSelectedSubTab('Site Details')}
          />
        </>
      )}

      {selectedSubTab === 'Plugins' && (
        <>
          <div className="mb-4 rounded border border-gray-700 bg-gray-900/50 p-4 text-white">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="m-0 text-lg font-semibold">Enabled Plugins</h3>
                <p className="mt-1 text-sm text-gray-300">
                  Enable live plugins here to reveal their dedicated tabs in the main control panel.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <label className="flex items-center gap-3 rounded bg-gray-800 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={enabledPluginKeys.includes('karaoke')}
                    onChange={(event) => togglePluginEnabled('karaoke', event.target.checked)}
                  />
                  <span>Karaoke</span>
                </label>
                <label className="flex items-center gap-3 rounded bg-gray-800 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={enabledPluginKeys.includes('jukebox')}
                    onChange={(event) => togglePluginEnabled('jukebox', event.target.checked)}
                  />
                  <span>Jukebox</span>
                </label>
                <label className="flex items-center gap-3 rounded bg-gray-800 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={enabledPluginKeys.includes('fixtures')}
                    onChange={(event) => togglePluginEnabled('fixtures', event.target.checked)}
                  />
                  <span>Fixtures</span>
                </label>
              </div>
            </div>
          </div>
          <Plugins
            siteId={site?.id || null}
            deviceId={currentDevice?.id || null}
            openEventInstanceId={openEventInstanceId}
            onConsumeOpenRequest={onConsumeOpenEventRequest}
            mode="settings"
          />
        </>
      )}

      {selectedSubTab === 'AI Agents' && showAiAgentsTab && (
        <>
          {aiAgentsSite ? (
            <DestinationSetup
              site={aiAgentsSite}
              refreshSitesAreasDevices={refreshSitesAreasDevices}
              onUpdateSiteFields={onUpdateSiteFields}
              initialSetupTab="AI Agents"
              hideSetupTabs
            />
          ) : (
            renderCreateDestinationEmptyState(
              'AI Agents',
              'AI Agents now save as one admin-controlled master setting for the whole webserver. Create the first destination here to open the AI Agents workspace.'
            )
          )}
        </>
      )}

      {/* Onboarding Wizard Modal */}
      {showOnboardingModal && (
        <div className="modal">
          <div className="modal-content settings-modal-content settings-modal-content--medium">
            <h3>
              {onboardingStep === 'initial' && 'Attempting Connection…'}
              {onboardingStep === 'prompt' && 'Allow on TV…'}
              {onboardingStep === 'debug' && 'Enable USB Debugging'}
              {onboardingStep === 'diagnose' && 'Troubleshoot Connection'}
              {onboardingStep === 'success' && 'Onboarding Complete!'}
              {onboardingStep === 'error' && 'Error During Onboarding'}
            </h3>

            <pre className="settings-modal-log">
              {onboardingLogs.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </pre>

            {onboardingStep === 'prompt' && (
              <>
                <p>Please use your TV remote to Allow USB debugging and tick “Always allow.”</p>
                <button
                  className="settings-modal-button primary"
                  onClick={async () => {
                    const ctrlId = devices.find(d => d.type && d.type.startsWith('controller')).id;
                    setOnboardingLogs(logs => [...logs, '⏳ Waiting for TV to finalize authorization…']);
                    await new Promise(res => setTimeout(res, 1500));
                    runDevicesCheck(ctrlId, onboardingIP, newDeviceId);
                  }}
                >
                  I’ve Allowed It
                </button>
                <button className="settings-modal-button secondary" onClick={() => setOnboardingStep('diagnose')}>
                  I don’t see a prompt
                </button>
              </>
            )}

            {onboardingStep === 'debug' && (
              <>
                <p>
                  USB debugging appears off. On your TV go to Settings → Device Preferences → Developer options and enable Debugging.
                </p>
                <button
                  className="settings-modal-button primary"
                  onClick={() =>
                    runInitialConnect(
                      devices.find(d => d.type && d.type.startsWith('controller')).id,
                      onboardingIP,
                      newDeviceId
                    )
                  }
                >
                  Retry Connection
                </button>
              </>
            )}

            {onboardingStep === 'diagnose' && (
              <>
                <p>Try one of the quick fixes below, then click “Retry Connection.”</p>
                <ul>
                  <li>Revoke authorizations on TV</li>
                  <li>Toggle USB debugging off/on</li>
                  <li>Reboot TV</li>
                </ul>
                <button
                  className="settings-modal-button primary"
                  onClick={() =>
                    runInitialConnect(
                      devices.find(d => d.type && d.type.startsWith('controller')).id,
                      onboardingIP,
                      newDeviceId
                    )
                  }
                >
                  Retry Connection
                </button>
              </>
            )}

            {onboardingStep === 'success' && (
              <>
                <p>🎉 Your TV is now onboarded!</p>
                <button
                  className="settings-modal-button primary"
                  onClick={() => {
                    setShowOnboardingModal(false);
                    setOnboardingStep(null);
                    setOnboardingLogs([]);
                    if (site?.id) refreshSitesAreasDevices(site.id, area?.id, true, newDeviceId);
                  }}
                >
                  Done
                </button>
              </>
            )}
            {onboardingStep !== 'success' && (
              <div className="modal-footer">
                <button className="settings-modal-button secondary" onClick={cancelOnboarding}>
                  Cancel Onboarding
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showAppSelectionModal && (
        <div className="modal">
          <div className="modal-content settings-modal-content settings-modal-content--wide">
            <h3>Select Apps to Control</h3>

            {appSelectionStatus === 'loading' && (
              <>
                <p>Retrieving installed apps from the device…</p>
                {!socket && <p className="settings-modal-error">Socket not connected; launch list request may fail.</p>}
                <p className="settings-modal-muted">If nothing appears, open the app on the TV and try again.</p>
              </>
            )}

            {appSelectionStatus === 'ready' && (
              <>
                <p>Tick the apps you want to be able to launch from the control panel.</p>
                <div className="settings-modal-scroll">
                  {appSelectionApps
                    .filter((a) => !isSuitestreamApp(a))
                    .filter((a) => a.launchable)
                    .map((app) => {
                      const pkg = app.package_name;
                      const label = app.app_name || pkg;
                      const checked = appSelectionEnabled.has(pkg);
                      return (
                        <label key={pkg} className="settings-app-row">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = new Set(appSelectionEnabled);
                              if (e.target.checked) next.add(pkg);
                              else next.delete(pkg);
                              setAppSelectionEnabled(next);
                            }}
                          />
                          {app.icon_url && (
                            <img
                              src={app.icon_url}
                              alt=""
                              className="settings-app-icon"
                            />
                          )}
                          <span className="settings-app-label">{label}</span>
                          <span className="settings-app-pkg">{pkg}</span>
                        </label>
                      );
                    })}
                </div>
              </>
            )}

            {appSelectionError && <p className="settings-modal-error">{appSelectionError}</p>}

            <div className="modal-footer">
              <button
                className="settings-modal-button secondary"
                type="button"
                onClick={() => {
                  sendDeviceCommand(appSelectionDeviceId, 'list_apps');
                  setAppSelectionStatus('loading');
                  setAppSelectionError('');
                }}
                disabled={!appSelectionDeviceId}
              >
                Refresh List
              </button>
              <button className="settings-modal-button secondary" type="button" onClick={closeAppSelection}>
                Skip for now
              </button>
              <button
                className="settings-modal-button primary"
                type="button"
                onClick={async () => {
                  const token = localStorage.getItem('token');
                  if (!token || !appSelectionDeviceId) return;
                  setAppSelectionStatus('saving');
                  setAppSelectionError('');
                  try {
                    const res = await fetch('/api/devices/set-enabled-apps', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({
                        deviceId: appSelectionDeviceId,
                        enabledPackages: Array.from(appSelectionEnabled),
                      }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.success) {
                      setAppSelectionStatus('ready');
                      setAppSelectionError(data.message || 'Failed to save app selection');
                      return;
                    }
                    closeAppSelection();
                  } catch (err) {
                    setAppSelectionStatus('ready');
                    setAppSelectionError('Failed to save app selection');
                  }
                }}
                disabled={appSelectionStatus !== 'ready'}
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {lgEditDevice && <LgDeviceAppEditor key={`${site?.id}:${area?.id}:${lgEditDevice.id}`} device={lgEditDevice} siteId={site?.id} onClose={() => setLgEditDevice(null)} />}
      {playerEditDevice && <div className="modal" role="dialog" aria-modal="true" aria-label="Player settings"><div className="modal-content settings-modal-content">
        <PlayerOnlySettings key={playerEditDevice.id} device={playerEditDevice} />
        <button type="button" onClick={() => setPlayerEditDevice(null)}>Close</button>
      </div></div>}

      {showDeviceEditModal && (
        <div className="modal">
          <div className="modal-content settings-modal-content settings-modal-content--wide">
            <h3>Edit Device</h3>
            <p className="settings-modal-muted">
              {deviceEditDevice?.name || 'Unnamed'} – {deviceEditDevice?.type}
            </p>

            <div className="device-add-startup-options">
              <label htmlFor="deviceEditEnsureBackgroundService" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  id="deviceEditEnsureBackgroundService"
                  type="checkbox"
                  checked={deviceEditEnsureBackgroundService}
                  onChange={(e) => setDeviceEditEnsureBackgroundService(e.target.checked)}
                />
                ensure background service is running
              </label>
              <p className="settings-modal-muted">Restarts the background service when disconnected, once ADB is set up. Requires the updated Suitestream app. Turn off for maintenance.</p>
              <p className="message">The background control agent starts in every mode. This setting controls the visible app after boot.</p>
              <label htmlFor="deviceEditStartupMode">Startup Mode</label>
              <select
                id="deviceEditStartupMode"
                value={deviceEditStartupMode}
                onChange={(e) => setDeviceEditStartupMode(e.target.value)}
              >
                <option value="NORMAL">NORMAL – agent only; launch display manually</option>
                <option value="AUTO_START">AUTO_START – agent and display on boot</option>
                <option value="LAUNCHER">LAUNCHER – auto-start and become Home app</option>
              </select>

              <label htmlFor="deviceEditAllowLocalMenu" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  id="deviceEditAllowLocalMenu"
                  type="checkbox"
                  checked={deviceEditAllowLocalMenu}
                  onChange={(e) => setDeviceEditAllowLocalMenu(e.target.checked)}
                />
                Allow hidden local admin menu (Back x7)
              </label>

              <label htmlFor="deviceEditMenuPin">Menu PIN (optional)</label>
              <input
                id="deviceEditMenuPin"
                type="password"
                inputMode="numeric"
                value={deviceEditMenuPin}
                onChange={(e) => setDeviceEditMenuPin(e.target.value)}
                placeholder="4 digits (leave blank for none)"
              />

            </div>

            <div className="settings-modal-section">
              <strong>Other Apps (launchable)</strong>
              {deviceEditStatus === 'loading' && <p className="settings-modal-muted">Retrieving installed apps from the device…</p>}
              <div className="settings-modal-scroll settings-modal-scroll--compact">
                {!deviceEditSelectableApps.length && deviceEditStatus === 'ready' && (
                  <div>No launchable apps found.</div>
                )}
                {deviceEditSelectableApps.map((app) => {
                  const pkg = app.package_name;
                  const label = app.app_name || pkg;
                  const checked = deviceEditEnabled.has(pkg);
                  return (
                    <label key={pkg} className="settings-app-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set(deviceEditEnabled);
                          if (e.target.checked) next.add(pkg);
                          else next.delete(pkg);
                          setDeviceEditEnabled(next);
                        }}
                      />
                      {app.icon_url && (
                        <img
                          src={app.icon_url}
                          alt=""
                          className="settings-app-icon"
                        />
                      )}
                      <span className="settings-app-label">{label}</span>
                      <span className="settings-app-pkg">{pkg}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {deviceEditError && <p className="settings-modal-error">{deviceEditError}</p>}

            <div className="modal-footer">
              <button
                className="settings-modal-button secondary"
                type="button"
                onClick={() => {
                  if (!deviceEditDevice?.id) return;
                  sendDeviceCommand(deviceEditDevice.id, 'list_apps');
                  setDeviceEditStatus('loading');
                  setDeviceEditError('');
                }}
                disabled={!deviceEditDevice?.id}
              >
                Refresh Apps From TV
              </button>
              <button className="settings-modal-button secondary" type="button" onClick={closeDeviceEdit} disabled={deviceEditStatus === 'saving'}>
                Cancel
              </button>
              <button
                className="settings-modal-button primary"
                type="button"
                onClick={async () => {
                  const token = localStorage.getItem('token');
                  if (!token || !deviceEditDevice?.id) return;
                  setDeviceEditStatus('saving');
                  setDeviceEditError('');
                  try {
                    const updateRes = await fetch('/api/devices/update-settings', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({
                        deviceId: deviceEditDevice.id,
                        settings: {
                          startup_mode: deviceEditStartupMode,
                          allow_local_menu: deviceEditAllowLocalMenu,
                          ensure_background_service: deviceEditEnsureBackgroundService,
                          menu_pin: deviceEditMenuPin,
                        },
                      }),
                    });
                    const updateData = await updateRes.json();
                    if (!updateRes.ok || !updateData.success) {
                      setDeviceEditStatus('ready');
                      setDeviceEditError(updateData.message || 'Failed to save startup settings');
                      return;
                    }

                    // Startup/recovery settings must be saveable while the app
                    // is offline. Never clear app choices from an unloaded list.
                    if (deviceEditAppsLoaded) {
                      const appsRes = await fetch('/api/devices/set-enabled-apps', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                          deviceId: deviceEditDevice.id,
                          enabledPackages: Array.from(deviceEditEnabled),
                        }),
                      });
                      const appsData = await appsRes.json();
                      if (!appsRes.ok || !appsData.success) {
                        setDeviceEditStatus('ready');
                        setDeviceEditError(appsData.message || 'Failed to save app selection');
                        return;
                      }
                    }

                    if (site?.id) {
                      refreshSitesAreasDevices(site.id, area?.id, true, deviceEditDevice?.id || null);
                    }
                    closeDeviceEdit();
                  } catch (err) {
                    setDeviceEditStatus('ready');
                    setDeviceEditError('Failed to save changes');
                  }
                }}
                disabled={deviceEditStatus === 'saving'}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {reconnectDevice && (
        <div className="modal">
          <div className="modal-content settings-modal-content settings-modal-content--medium">
            <h3>Reconnect Device</h3>
            <p className="settings-modal-muted">
              {reconnectDevice.name || 'Unnamed'} – {reconnectDevice.type}
            </p>
            <p>
              Open the connection screen on this device, then enter the six-digit verification code shown there.
              Its existing settings and device history will be preserved.
            </p>
            <label htmlFor="reconnectDeviceCode">Verification code</label>
            <input
              id="reconnectDeviceCode"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={reconnectCode}
              onChange={(event) => {
                setReconnectCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                if (reconnectStatus === 'error') {
                  setReconnectStatus('idle');
                  setReconnectMessage('');
                }
              }}
              disabled={reconnectStatus === 'submitting' || reconnectStatus === 'success'}
              autoFocus
            />
            {reconnectMessage ? (
              <p className={reconnectStatus === 'error' ? 'settings-modal-error' : 'message'}>
                {reconnectMessage}
              </p>
            ) : null}
            <div className="modal-actions">
              <button
                type="button"
                className="settings-modal-button secondary"
                onClick={closeReconnectDevice}
                disabled={reconnectStatus === 'submitting'}
              >
                {reconnectStatus === 'success' ? 'Close' : 'Cancel'}
              </button>
              {reconnectStatus !== 'success' ? (
                <button
                  type="button"
                  className="settings-modal-button primary"
                  onClick={handleReconnectDevice}
                  disabled={reconnectStatus === 'submitting' || reconnectCode.length !== 6}
                >
                  {reconnectStatus === 'submitting' ? 'Reconnecting...' : 'Reconnect Device'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
